import { useMemo } from 'react';
import {
  App as AntApp, Col, DatePicker, Form, Input, InputNumber, Modal, Row, Select, Upload,
} from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  useCashCategories,
  useCreateCashTransaction,
  type CashDirection,
  type PaymentMethod,
} from '../api/cash';
import { CounterpartySelect } from './counterparty-select';
import { api, asApiError } from '@/shared/lib/api';

const METHODS: PaymentMethod[] = ['cash', 'bank_transfer', 'card'];

export function TransactionModal({
  open,
  direction,
  cashAccountId,
  onClose,
}: {
  open: boolean;
  direction: CashDirection;
  cashAccountId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const [form] = Form.useForm();
  const create = useCreateCashTransaction();
  const { data: categories } = useCashCategories();

  // System categories (e.g. apartment payment) are posted automatically from the
  // Payments module — they must never be picked for a manual cash entry.
  const applicable = useMemo(
    () => (categories ?? []).filter((c) => c.active && !c.isSystem && (c.scope === 'both' || c.scope === direction)),
    [categories, direction],
  );
  const categoryId = Form.useWatch('categoryId', form) as string | undefined;
  const selectedCategory = applicable.find((c) => c.id === categoryId);
  const requiresCounterparty = selectedCategory?.requiresCounterparty ?? false;

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: true,
    accept: 'image/jpeg,image/png,image/webp,application/pdf',
    beforeUpload(file) {
      if (file.size > 10 * 1024 * 1024) {
        void message.error(t('customers.passportFileTooLarge'));
        return Upload.LIST_IGNORE;
      }
      return true;
    },
    async customRequest({ file, onSuccess, onError }) {
      try {
        const fd = new FormData();
        fd.append('file', file as Blob);
        const res = await api.post('/uploads/file', fd, { params: { purpose: 'cash' } });
        onSuccess?.(res.data);
      } catch (err) {
        onError?.(axios.isAxiosError(err) ? new Error(err.message) : (err as Error));
      }
    },
  };

  async function onOk() {
    try {
      const v = await form.validateFields();
      const fileList: UploadFile[] = Array.isArray(v.attachments) ? v.attachments : [];
      const attachmentUrls = fileList
        .map((f) => (f.response as { data?: { key?: string } } | undefined)?.data?.key)
        .filter((k): k is string => !!k);

      await create.mutateAsync({
        cashAccountId,
        direction,
        amount: String(BigInt(Math.round(Number(v.amount) * 100))),
        txnDate: v.txnDate.format('YYYY-MM-DD'),
        categoryId: v.categoryId,
        method: v.method,
        counterpartyId: v.counterpartyId || undefined,
        description: v.description || undefined,
        attachmentUrls,
      });
      void message.success(direction === 'income' ? t('kassa.incomeAdded') : t('kassa.expenseAdded'));
      onClose();
      form.resetFields();
    } catch (err) {
      if ((err as { errorFields?: unknown[] }).errorFields) return;
      void message.error(asApiError(err).message);
    }
  }

  return (
    <Modal
      title={direction === 'income' ? t('kassa.newIncome') : t('kassa.newExpense')}
      open={open}
      onOk={onOk}
      onCancel={() => { onClose(); form.resetFields(); }}
      confirmLoading={create.isPending}
      okText={t('common.create')}
      cancelText={t('common.cancel')}
      okButtonProps={{ style: direction === 'income' ? {} : { background: '#dc2626', borderColor: '#dc2626' } }}
      destroyOnHidden
      width={560}
    >
      <Form form={form} layout="vertical" requiredMark={false} initialValues={{ txnDate: dayjs(), method: 'cash' }}>
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item name="amount" label={t('kassa.amount')} rules={[{ required: true, type: 'number', min: 1 }]}>
              <InputNumber<number>
                style={{ width: '100%' }}
                min={1}
                step={100000}
                formatter={(v) => `${v ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                parser={(v) => Number((v ?? '').replace(/\s/g, '')) as number}
                addonAfter="UZS"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="txnDate" label={t('kassa.date')} rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item name="categoryId" label={t('kassa.category')} rules={[{ required: true }]}>
              <Select
                placeholder="—"
                options={applicable.map((c) => ({ value: c.id, label: c.name }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="method" label={t('kassa.method')} rules={[{ required: true }]}>
              <Select options={METHODS.map((m) => ({ value: m, label: t(`apartments.paymentMethods.${m}`) }))} />
            </Form.Item>
          </Col>
        </Row>

        {requiresCounterparty && (
          <Form.Item
            name="counterpartyId"
            label={t('kassa.counterparty')}
            rules={[{ required: true, message: t('kassa.counterpartyRequired') }]}
          >
            <CounterpartySelect />
          </Form.Item>
        )}

        <Form.Item name="description" label={t('kassa.description')}>
          <Input.TextArea rows={2} maxLength={2000} />
        </Form.Item>

        <Form.Item
          name="attachments"
          label={t('kassa.attachments')}
          valuePropName="fileList"
          getValueFromEvent={(e) => (Array.isArray(e) ? e : (e?.fileList ?? []))}
        >
          <Upload.Dragger {...uploadProps} style={{ padding: 8 }}>
            <p className="ant-upload-drag-icon" style={{ margin: 0 }}><InboxOutlined /></p>
            <p style={{ fontSize: 12, margin: 0 }}>{t('customers.uploadPassportDrop')}</p>
            <p style={{ fontSize: 11, color: '#94a3b8', margin: 0 }}>{t('customers.uploadPassportFormats')}</p>
          </Upload.Dragger>
        </Form.Item>
      </Form>
    </Modal>
  );
}
